/** Schema.org JSON-LD helpers for PinnacleBuy */

const BASE_URL = 'https://pinnaclebuy.com';

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PinnacleBuy',
  url: BASE_URL,
  description:
    'Independent comparison site for futures proprietary trading firms. Find the best-value prop firm with exclusive discount codes.',
  logo: `${BASE_URL}/og-image.png`,
});

export const breadcrumbSchema = (
  items: Array<{ name: string; url?: string }>
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    ...(item.url ? { item: `${BASE_URL}${item.url}` } : {}),
  })),
});

interface ProductSchemaOptions {
  name: string;
  description: string;
  url: string;
  offers?: Array<{ price: number }>;
}

export const productSchema = ({
  name,
  description,
  url,
  offers,
}: ProductSchemaOptions) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  url: `${BASE_URL}${url}`,
  brand: {
    '@type': 'Organization',
    name,
  },
  ...(offers && offers.length > 0
    ? {
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'USD',
          lowPrice: Math.min(...offers.map((o) => o.price)).toString(),
          highPrice: Math.max(...offers.map((o) => o.price)).toString(),
          offerCount: offers.length.toString(),
        },
      }
    : {}),
});

export interface FAQItem {
  question: string;
  answer: string;
}

export interface ItemListFirm {
  name: string;
  slug: string;
  notable_features?: string | null;
  funding_programs: Array<{
    discounted_price?: number | null;
    base_price?: number | null;
  }>;
}

export const itemListSchema = (
  listName: string,
  firms: ItemListFirm[]
) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: listName,
  itemListOrder: 'https://schema.org/ItemListOrderDescending',
  numberOfItems: firms.length,
  itemListElement: firms.map((firm, index) => {
    const offers = firm.funding_programs
      .map((p) => p.discounted_price ?? p.base_price)
      .filter((p): p is number => p != null)
      .map((price) => ({ price }));
    return {
      '@type': 'ListItem',
      position: index + 1,
      item: productSchema({
        name: firm.name,
        description:
          firm.notable_features || `${firm.name} futures prop trading firm`,
        url: `/firms/${firm.slug}`,
        offers,
      }),
    };
  }),
});

export const faqSchema = (items: FAQItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});
